import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { gallery } from "../data/profileData";
import ChapterMark from "../components/ChapterMark";
import { X } from "lucide-react";
import Seo from "../components/Seo";

export default function Gallery() {
  const [active, setActive] = useState(null);

  return (
    <div data-testid="gallery-page" className="bg-white">
      <Seo
        path="/gallery"
        title="Gallery"
        description="Moments of meaning and milestone — a curated photo gallery of Dr. P. B. Boss across awards, partnerships and the journey of the Osaka Group."
      />
      <section
        data-testid="gallery-hero"
        className="pt-44 pb-24 lg:pt-52 lg:pb-32 bg-white relative"
      >
        <span className="hidden lg:block absolute right-7 top-1/2 -translate-y-1/2 v-label">
          P · B · Boss — Gallery
        </span>
        <div className="max-w-[1640px] mx-auto px-8 sm:px-14 lg:px-20 text-center">
          <ChapterMark numeral="IV" label="The Gallery" />
          <h1 className="mt-14 font-couture text-[clamp(3.5rem,9vw,8rem)] leading-[0.94] tracking-[-0.025em] text-[var(--ink)]">
            Moments of
            <br />
            <span className="font-couture-italic text-[var(--gold-deep)]">
              meaning & milestone.
            </span>
          </h1>
          <p className="mt-10 font-marcellus uppercase tracking-[0.42em] text-[0.66rem] text-[var(--gold-deep)]">
            {gallery.length} Frames
          </p>
        </div>
      </section>

      {/* edge-to-edge masonry */}
      <section data-testid="gallery-grid" className="py-12 lg:py-16 bg-[var(--bg-soft)]">
        <div className="px-4 sm:px-8 lg:px-12">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 lg:gap-4">
            {gallery.map((src, i) => {
              const span = i % 7 === 0;
              return (
                <motion.button
                  key={src + i}
                  data-testid={`gallery-item-${i}`}
                  onClick={() => setActive(src)}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.6, ease: [0.2, 0.7, 0.2, 1] }}
                  className={`group relative overflow-hidden img-frame bg-white aspect-square ${
                    span ? "lg:row-span-2 lg:aspect-[3/4]" : ""
                  }`}
                  aria-label={`Open image ${i + 1}`}
                >
                  <img
                    src={src}
                    alt={`Frame ${i + 1}`}
                    className="absolute inset-0 w-full h-full object-cover img-soft"
                    loading="lazy"
                  />
                  <span className="absolute inset-0 bg-[var(--bg-ink)]/0 group-hover:bg-[var(--bg-ink)]/25 transition-colors duration-700" />
                  <span className="absolute bottom-3 left-3 font-marcellus uppercase tracking-[0.34em] text-[10px] text-white opacity-0 group-hover:opacity-100 transition-opacity duration-700">
                    № {String(i + 1).padStart(3, "0")}
                  </span>
                </motion.button>
              );
            })}
          </div>
        </div>
      </section>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="gallery-lightbox"
            className="fixed inset-0 z-[60] bg-[var(--bg-ink)]/95 backdrop-blur-md flex items-center justify-center p-7"
            onClick={() => setActive(null)}
            role="dialog"
            aria-modal="true"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.2, 0.7, 0.2, 1] }}
          >
            <button
              data-testid="gallery-lightbox-close"
              className="absolute top-7 right-7 w-12 h-12 inline-flex items-center justify-center border border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-[var(--bg-ink)] transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                setActive(null);
              }}
              aria-label="Close"
            >
              <X size={18} />
            </button>
            <motion.img
              src={active}
              alt="Selected"
              className="max-w-[92vw] max-h-[88vh] object-contain"
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.5, ease: [0.2, 0.7, 0.2, 1] }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
